import './InstanceViewerWithTree.scss';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Splitter } from 'components/Splitter/Splitter';
import InstanceViewer from '../InstanceViewer';
import { NodeNavigationContext, type NodeNavigation } from '../NodeNavigationContext';
import { InstanceTreeModel } from './InstanceTreeModel';
import { InstanceAncestorTree } from './InstanceAncestorTree';

const DEFAULT_TREE_WIDTH = 420;
const MIN_TREE_WIDTH = 260;
const MAX_TREE_WIDTH = 640;

/**
 * The node-instance show page: the ancestor + siblings tree navigator beside
 * the existing InstanceViewer. The model is keyed on the entry id (route param
 * or `bottom_panel_id`), so it survives in-tree navigation — clicking a node
 * calls setActive, which drives the viewer via its node_instance_id prop
 * without touching the URL. The model's refreshToken reloads the viewer when
 * the id alone does not change, and a change the viewer makes refreshes the
 * tree. An out-of-tree link opens a fresh tab (and thus its own tree); the entry
 * id only changes on a genuine new entry, which re-keys a fresh model.
 */
export const InstanceViewerWithTree = observer(({ entryId }: { entryId?: number | string }) => {
  const params = useParams<{ nodeInstanceId?: string }>();
  const resolvedEntryId = Number(entryId ?? params.nodeInstanceId);
  const [treeWidth, setTreeWidth] = useState(DEFAULT_TREE_WIDTH);

  const model = useMemo(() => new InstanceTreeModel(resolvedEntryId), [resolvedEntryId]);

  const nav = useMemo<NodeNavigation>(
    () => ({
      // `kind` is intentionally ignored here: this provider routes by tree
      // membership, not relationship. In-tree targets (any kind) soft-swap
      // the active instance; anything out of tree — including an out-of-tree
      // parent — opens its own fresh tree at /instance_viewer/:id, rather
      // than the default provider's per-kind behaviour (parent → the
      // parent-scoped list page). The param stays in the signature so callers
      // keep passing kind for the default, list-page provider.
      open: (id, _kind) => {
        const numericId = Number(id);
        if (!model.tryActivate(numericId)) {
          window.open(`/instance_viewer/${numericId}`, '_blank', 'noopener, noreferrer');
        }
      },
    }),
    [model]
  );

  if (!Number.isFinite(resolvedEntryId)) {
    return <InstanceViewer node_instance_id={entryId} />;
  }

  return (
    <NodeNavigationContext.Provider value={nav}>
      <div className="instance-viewer-with-tree">
        <div className="instance-viewer-tree-pane" style={{ width: treeWidth }}>
          <InstanceAncestorTree model={model} />
        </div>
        <Splitter size={treeWidth} onResize={setTreeWidth} min={MIN_TREE_WIDTH} max={MAX_TREE_WIDTH} side="before" ariaLabel="Resize tree navigator" />
        <div className="instance-viewer-content-pane">
          <InstanceViewer node_instance_id={model.activeId} refreshToken={model.refreshToken} onInstanceChanged={model.refresh} />
        </div>
      </div>
    </NodeNavigationContext.Provider>
  );
});

export default InstanceViewerWithTree;
